import { THEME_VARIABLE } from '@/config';
import { Flex, Input, Modal, Typography, Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { Heading } from '../../Heading';
import { CheckIcon, DotIcon, EditOutline, EyeIcon, PictureOutline } from '../../Icons';
import { humanizeKey } from '@/utils';
import { Card } from '@/components/Card';
import ImageRequirementCard from '../ImageRequirementCard';
import { useEffect, useState } from 'react';

enum TrackingTheme {
  PRIMARY = 'customYellowPrimary',
  BLUE = 'customBlue',
  GREEN = 'customGreen',
  ORANGE = 'customOrange',
  VIOLET = 'customViolet',
  RED = 'customRed',
  BROWN = 'customColorsBrown',
}

const TeamMemberSection = () => {
  const [selectedColor, setSelectedColor] = useState(TrackingTheme.PRIMARY);
  const [showPreview, setShowPreview] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  const { t } = useTranslation();

  const trackingTheme = {
    [TrackingTheme.PRIMARY]: THEME_VARIABLE.customYellowPrimary,
    [TrackingTheme.BLUE]: THEME_VARIABLE.customBlue,
    [TrackingTheme.GREEN]: THEME_VARIABLE.customGreen,
    [TrackingTheme.ORANGE]: THEME_VARIABLE.customOrange,
    [TrackingTheme.VIOLET]: THEME_VARIABLE.customViolet,
    [TrackingTheme.RED]: THEME_VARIABLE.customRed,
    [TrackingTheme.BROWN]: THEME_VARIABLE.customColorsBrown,
  };

  const close = () => {
    setAnimateOut(true);
  };

  useEffect(() => {
    if (!animateOut) return;
    const timer = setTimeout(() => {
      setShowPreview(false);
      setAnimateOut(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [animateOut]);

  return (
    <Flex vertical>
      <Flex justify='space-between' align='center'>
        <Heading title={t('company_info')} fontSize='text-lg' fontWeight='font-medium' />
        <Flex justify='end' gap={16}>
          <button
            type='button'
            onClick={() => setShowPreview(true)}
            className='flex items-center gap-1.5 h-9 px-4 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors'
          >
            <EyeIcon />
            <Typography.Text>{humanizeKey(t('preview'))}</Typography.Text>
          </button>
        </Flex>
      </Flex>
      <Card extraClassName='mt-4 gap-3'>
        <ImageRequirementCard
          handleAvatarUpload={() => {}}
          isAvatarUploading={false}
          avatarUrl={undefined}
        />
        <Flex
          vertical
          className='border-solid border-2 border-customGrayShade3 rounded-lg p-2 h-60'
        >
          <Flex className='absolute cursor-pointer flex justify-center items-center w-[32px] h-[32px] rounded-full bg-customYellowPrimary border border-solid border-white'>
            <Upload
              beforeUpload={() => false} // Prevent automatic upload
              onChange={() => {}} // Use parent's upload handler
              showUploadList={false}
            >
              <button
                type='button'
                className='flex items-center justify-center p-0 border-none bg-transparent cursor-pointer'
              >
                <EditOutline />
              </button>
            </Upload>
          </Flex>
          <Flex className='w-full h-full' justify='center' align='center'>
            <PictureOutline />
          </Flex>
        </Flex>
        <Flex vertical className='mx-[27px]'>
          <Heading
            title={t('image_requirement')}
            fontSize='text-xs'
            fontWeight='font-normal'
            color='text-customGrayShade5'
            extraClassName='leading-[14.4px] tracking-[-.32px] capitalize mb-[6px]'
          />
          <Heading
            leadingIcon={<DotIcon color={THEME_VARIABLE.customTunaPrimary} />}
            title={t('supported_formats')}
            fontSize='text-xs'
            fontWeight='font-normal'
            color='text-customTunaPrimary'
            extraClassName='leading-4 tracking-[-.32px] capitalize'
          />
          <Heading
            leadingIcon={<DotIcon color={THEME_VARIABLE.customTunaPrimary} />}
            title={t('maximum_file_size')}
            fontSize='text-xs'
            fontWeight='font-normal'
            color='text-customTunaPrimary'
            extraClassName='leading-4 tracking-[-.32px] capitalize'
          />
          <Heading
            leadingIcon={<DotIcon color={THEME_VARIABLE.customTunaPrimary} />}
            title={t('aspect_ratio_should_be_square_1_9_aspect_ratio')}
            fontSize='text-xs'
            fontWeight='font-normal'
            color='text-customTunaPrimary'
            extraClassName='leading-4 tracking-[-.32px] capitalize'
          />
          <Heading
            leadingIcon={<DotIcon color={THEME_VARIABLE.customTunaPrimary} />}
            title={t('minimum_recommended_resolution')}
            fontSize='text-xs'
            fontWeight='font-normal'
            color='text-customTunaPrimary'
            extraClassName='leading-4 tracking-[-.32px] capitalize'
          />
        </Flex>
        <Flex vertical>
          <Heading
            title={t('tracking_page_theme')}
            fontSize='text-xs'
            fontWeight='font-normal'
            color='text-customGrayShade5'
            extraClassName='leading-[14.4px] tracking-[-.32px] capitalize mb-[6px]'
          />
          <Flex gap={8}>
            {Object.entries(trackingTheme).map(([enumColor, colorCode], index) => (
              <div
                className={`w-10 h-10 rounded-lg bg-${enumColor} cursor-pointer`}
                onClick={() => setSelectedColor(enumColor as TrackingTheme)}
                style={{ backgroundColor: colorCode }}
                key={index}
              >
                {selectedColor === enumColor && (
                  <CheckIcon
                    className='m-[10px]'
                    width={20}
                    height={20}
                    color={THEME_VARIABLE.customTunaPrimary}
                  />
                )}
              </div>
            ))}
          </Flex>
        </Flex>
        <Input
          placeholder={t('brand_name')}
          variant='borderless'
          className='border border-solid placeholder:capitalize placeholder:font-normal placeholder:text-base placeholder:leading-[20.48px] placeholder:text-GrayShade4 border-customGrayShade5 rounded-lg h-[56px]'
        />
        <Input
          placeholder={t('website_url')}
          variant='borderless'
          className='border border-solid placeholder:capitalize placeholder:font-normal placeholder:text-base placeholder:leading-[20.48px] placeholder:text-GrayShade4 border-customGrayShade5 rounded-lg h-[56px]'
        />
        <Input
          placeholder={t('support_phone_number')}
          variant='borderless'
          className='border border-solid placeholder:capitalize placeholder:font-normal placeholder:text-base placeholder:leading-[20.48px] placeholder:text-GrayShade4 border-customGrayShade5 rounded-lg h-[56px]'
        />
      </Card>
      <Heading
        title={t('social_media_links')}
        extraClassName='mt-4'
        fontSize='text-lg'
        fontWeight='font-medium'
      />

      <Card extraClassName='mt-4 gap-3'>
        <Input
          placeholder={t('facebook_url')}
          variant='borderless'
          className='border border-solid placeholder:capitalize placeholder:font-normal placeholder:text-base placeholder:leading-[20.48px] placeholder:text-GrayShade4 border-customGrayShade5 rounded-lg h-[56px]'
        />
        <Input
          placeholder={t('instagram_url')}
          variant='borderless'
          className='border border-solid placeholder:capitalize placeholder:font-normal placeholder:text-base placeholder:leading-[20.48px] placeholder:text-GrayShade4 border-customGrayShade5 rounded-lg h-[56px]'
        />
        <Input
          placeholder={t('pinterest_url')}
          variant='borderless'
          className='border border-solid placeholder:capitalize placeholder:font-normal placeholder:text-base placeholder:leading-[20.48px] placeholder:text-GrayShade4 border-customGrayShade5 rounded-lg h-[56px]'
        />
      </Card>

      {(showPreview || animateOut) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            animation: animateOut ? 'fadeOut 0.3s ease-out' : 'fadeIn 0.3s ease-out',
          }}
        >
          <iframe
            src={`https://tracking-stg.tryboxy.dev/preview?themeColor=${encodeURIComponent(
              selectedColor, //TODO: It's stg env
            )}`}
            style={{
              width: '393px',
              height: '90%',
              border: 'none',
              borderRadius: '12px',
              background: 'white',
              animation: animateOut ? 'scaleOut 0.3s ease-out' : 'scaleIn 0.3s ease-out',
            }}
          />
          <button
            onClick={close}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '2rem',
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      )}
    </Flex>
  );
};

export default TeamMemberSection;
